'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Footer, H2, Li, Nav, P, Span, Ul } from '@/components/ui';
import { footerColumns } from '@/constants/footerMenu';
import AppLink from '@/components/i18n/AppLink';
import { SITE_CONTACT_EMAIL, SITE_CONTACT_MAILTO } from '@/constants/siteContact';
import { useNavPathLabel } from '@/hooks/useNavPathLabel';

const FooterUI: React.FC = () => {
  const year = new Date().getFullYear();
  const t = useTranslations('layout');
  const tFooter = useTranslations('footer.columns');
  const resolveNavLabel = useNavPathLabel();

  return (
    <Footer className="home-footer site-footer" aria-label={t('footerAria')}>
      <div className="site-footer__inner">
        <div className="site-footer__grid">
          {footerColumns.map((col) => {
            const colTitle = tFooter(col.titleKey);
            return (
              <div key={col.titleKey} className="site-footer__col">
                <H2 className="site-footer__col-title">{colTitle}</H2>
                <Ul className="site-footer__list" aria-label={colTitle}>
                  {col.links.map((item) => {
                    const label = item.label ?? resolveNavLabel({ href: item.href });
                    return (
                      <Li key={item.href}>
                        <AppLink
                          href={item.href}
                          external={item.external}
                          className="site-footer__link"
                          aria-label={label}
                          title={label}
                        >
                          {label}
                        </AppLink>
                      </Li>
                    );
                  })}
                </Ul>
              </div>
            );
          })}
        </div>
      </div>

      <div className="site-footer__legal">
        <div className="site-footer__inner site-footer__legal-inner">
          <P className="site-footer__copy">
            {t('footerReach')}{' '}
            <a href={SITE_CONTACT_MAILTO} className="site-footer__inline-link">
              {SITE_CONTACT_EMAIL}
            </a>
          </P>
          <P className="site-footer__copy-muted">{t('footerCopyright', { year })}</P>
          <Nav className="site-footer__legal-nav" aria-label={t('footerLegal')}>
            <AppLink href="/pricing" className="site-footer__legal-link" title={t('footerPricing')}>
              {t('footerPricing')}
            </AppLink>
            <Span className="site-footer__legal-sep" aria-hidden>
              |
            </Span>
            <AppLink href="/privacy" className="site-footer__legal-link" title={t('footerPrivacy')}>
              {t('footerPrivacy')}
            </AppLink>
            <Span className="site-footer__legal-sep" aria-hidden>
              |
            </Span>
            <AppLink href="/help" className="site-footer__legal-link" title={t('footerHelp')}>
              {t('footerHelp')}
            </AppLink>
            <Span className="site-footer__legal-sep" aria-hidden>
              |
            </Span>
            <AppLink href="/sitemap" className="site-footer__legal-link" title={t('footerSitemap')}>
              {t('footerSitemap')}
            </AppLink>
          </Nav>
        </div>
      </div>
    </Footer>
  );
};

export default FooterUI;
